import React, { useEffect, useRef } from "react";
import { Box, Button, Stack } from "@mui/joy";
import { Navigate, useParams, useSearchParams } from "react-router-dom";
import mixpanel from "mixpanel-browser";
import { theme } from "styles/theme";
import FullPageContainer from "pages/container/FullPageContainer";
import CustomTypography from "components/common/CustomTypography";
import { LogoWithTextSvg } from "static/image/common/SvgAssets";
import { deepLinkData } from "data/deepLinkData";
import useMixpanelWeb from "hooks/useMixpanelWeb";

const KAKAO_CHANNEL_PUBLIC_ID = "_xjxfLVG";
const KAKAO_CHANNEL_LINK_TEXT = "디스턴싱 카카오채널";

// 이전 레터 다시보기 링크 라벨용 서수 (새 레터 추가 시 함께 확장)
const LETTER_ORDINALS: Record<string, string> = {
  "1": "첫",
  "2": "두",
  "3": "세",
};

// 발송 시 URL 에 실어주는 trackId(Mixpanel distinct_id) 형식 — 형식이 맞을 때만 식별·트래킹한다
const TRACK_ID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// 가입 환영 레터 시리즈 (인증 없이 접근 가능한 원페이지).
// 새 레터는 LETTERS 에 항목만 추가하면 /letter/<key> 로 열린다.
type LetterContent = {
  /** 다시보기 링크에 노출되는 핵심 내용 요약 */
  title: string;
  greeting?: string;
  paragraphsBeforeVideo: string[];
  youtubeVideoId: string;
  paragraphsAfterVideo: string[];
};

const LETTERS: Record<string, LetterContent> = {
  "1": {
    title: "나 자신의 치료자가 되기",
    greeting: "안녕하세요, 디스턴싱에 가입하신 것을 환영합니다.",
    paragraphsBeforeVideo: [
      "디스턴싱에 가입하셨다는 건, 지금보다 더 나은 삶을 만들어보고 싶은 마음이 있었기 때문이라고 생각해요. 저희가 그 과정에서 최선을 다해 돕겠습니다.",
      "내 삶의 문제를 궁극적으로 해결하고 회복하기 위해 가장 중요한 것은, 결국 '내가 나 자신의 치료자가 되는 것'입니다. 마법처럼 단번에 삶을 바꿔주는 약은 없지만, 내가 스스로를 돌보기로 선택하고 노력한다면 분명 변화할 수 있을 거예요. 아래 유튜브 영상에서 이에 대해 조금 더 자세히 이야기를 해 보았답니다.",
    ],
    youtubeVideoId: "lduFQzN4oxs",
    paragraphsAfterVideo: [
      "앞으로 총 세 번의 영상을 통해 중요한 이야기들을 전하려고 해요. 앞으로 이어질 두 편의 영상도 받아보시려면 디스턴싱 카카오채널을 친구 추가해 주세요!",
    ],
  },
  "2": {
    title: "생각과 거리를 두는 법",
    paragraphsBeforeVideo: [
      "지난 영상에 이어, 이번에는 어떻게 '내 삶의 나침반이 되어 스스로를 돌볼 수 있는지' 그 원리를 말씀드리려 합니다.",
      "핵심은 내면의 괴로운 생각에 휘둘리지 않고, 한 걸음 물러서서 '생각과 거리를 두는 마음의 힘'을 기르는 것입니다. 물론 그 과정이 쉽진 않답니다. 때론 불편하기도 할 거예요. 하지만 하나씩 노력한다면 분명 마음의 근육을 키울 수 있는 방법이 있답니다.",
      "이번 영상에서는 디스턴싱에서 무엇을 하고, 어떻게 프로그램을 진행하는지에 대해 간단히 소개해 보았어요.",
    ],
    youtubeVideoId: "TKj_SzNHQw8",
    paragraphsAfterVideo: [],
  },
  "3": {
    title: "원하는 삶으로 나아가기",
    paragraphsBeforeVideo: [
      "혹시 그동안 부정적인 감정을 '없애는 것'에만 온 마음을 쏟고 계시진 않았나요? 하지만 우리 삶의 진짜 목적은 우울과 불안을 지우는 것이 아니라, '내가 진정으로 원하는 삶을 살아나가는 것'입니다. 괴로운 감정이 찾아오더라도 휘둘리지 않고, 내게 소중한 가치를 향해 한 걸음씩 나아갈 수 있는 힘은 이미 여러분 안에 있습니다.",
      "여러분이 우울을 넘어 '원하는 삶'으로 전념할 수 있도록 돕는 마지막 이야기를 지금 확인해 보세요. 여러분이 언제든 변화를 선택하신다면, 디스턴싱은 늘 이곳에서 최선을 다해 도울 준비가 되어 있습니다.",
    ],
    youtubeVideoId: "QZkd50OOH_g",
    paragraphsAfterVideo: [],
  },
};

function YoutubeEmbed({ videoId }: { videoId: string }) {
  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: 0,
        // 16:9 비율 확보 (aspect-ratio 미지원 구형 WebView 대응)
        paddingBottom: "56.25%",
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: theme.vars.palette.Surface.Backward,
      }}
    >
      <Box
        component="iframe"
        src={`https://www.youtube.com/embed/${videoId}`}
        title="유튜브 영상"
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
      />
    </Box>
  );
}

function KakaoChannelLink({
  letterId,
  trackId,
}: {
  letterId: string;
  trackId: string | null;
}) {
  const trackLinkClick = useMixpanelWeb({
    eventName: "레터 카카오 채널 추가 링크 클릭",
    params: { letter_id: letterId },
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (trackId) {
      trackLinkClick();
    }
    // SDK 초기화가 되어 있으면 공식 친구추가 다이얼로그, 아니면 href(채널 친구추가 웹 페이지)로 진행
    if (window.Kakao?.isInitialized?.()) {
      e.preventDefault();
      window.Kakao.Channel.addChannel({
        channelPublicId: KAKAO_CHANNEL_PUBLIC_ID,
      });
    }
  };

  return (
    <Box
      component="a"
      href={`https://pf.kakao.com/${KAKAO_CHANNEL_PUBLIC_ID}/friend`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handleClick}
      sx={{
        // highlight 색 + 밑줄로 링크 표시
        color: theme.vars.palette.Content.Highlight,
        textDecoration: "underline",
        textUnderlineOffset: "3px",
      }}
    >
      {KAKAO_CHANNEL_LINK_TEXT}
    </Box>
  );
}

// 문단 안의 "디스턴싱 카카오채널" 문구를 친구 추가 링크로 치환해 렌더
function renderParagraphWithChannelLink(
  paragraph: string,
  letterId: string,
  trackId: string | null
) {
  const parts = paragraph.split(KAKAO_CHANNEL_LINK_TEXT);
  if (parts.length === 1) {
    return paragraph;
  }
  return parts.map((part, index) => (
    <React.Fragment key={index}>
      {index > 0 && <KakaoChannelLink letterId={letterId} trackId={trackId} />}
      {part}
    </React.Fragment>
  ));
}

export default function LetterPage() {
  const { letterId } = useParams();
  const letter = letterId ? LETTERS[letterId] : undefined;

  // 발송 시 URL 에 실어준 ?u=<trackId>. 없거나 형식이 다르면 어떤 이벤트도 보내지 않는다
  const [searchParams] = useSearchParams();
  const uParam = searchParams.get("u");
  const trackId = uParam && TRACK_ID_REGEX.test(uParam) ? uParam : null;

  const trackEnter = useMixpanelWeb({
    eventName: "레터 페이지 진입",
    params: { letter_id: letterId ?? "" },
  });
  const trackCtaClick = useMixpanelWeb({
    eventName: "레터 디스턴싱 시작하기 버튼 클릭",
    params: { letter_id: letterId ?? "" },
  });

  // 마운트 1회 발사 (StrictMode 중복 방지) — SubscriptionPauseScreen 패턴.
  const enteredRef = useRef(false);
  useEffect(() => {
    if (!letter || !trackId || enteredRef.current) return;
    enteredRef.current = true;
    // track 이전에 식별 — 이 페이지의 이벤트가 기존 유저(trackId)로 귀속된다
    mixpanel.identify(trackId);
    // 유튜브·카카오채널 친구추가 등 외부로 나갔다 돌아오며 재로드돼도
    // 같은 탭 세션에서는 진입을 다시 집계하지 않는다
    const enteredKey = `letter_page_entered_${letterId}`;
    if (!sessionStorage.getItem(enteredKey)) {
      sessionStorage.setItem(enteredKey, "true");
      trackEnter();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!letter) {
    return <Navigate to="/" replace />;
  }

  // 이전 레터 다시보기 링크 대상 (레터 2 → 1, 레터 3 → 1·2)
  const previousLetterIds = Object.keys(LETTERS).filter(
    (id) => Number(id) < Number(letterId)
  );

  // 본문에 카카오채널 추가 요청이 있는 레터(현재 1번)는 채널 추가 전환에 집중하도록
  // 앱 진입 CTA 를 노출하지 않는다
  const hasKakaoAsk = [
    ...letter.paragraphsBeforeVideo,
    ...letter.paragraphsAfterVideo,
  ].some((paragraph) => paragraph.includes(KAKAO_CHANNEL_LINK_TEXT));

  return (
    <FullPageContainer
      backgroundColor={theme.vars.palette.Background.Level1}
      hasFooter={false}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          height: "100%",
          overflowY: "auto",
          px: 3,
          pt: "48px",
          pb: "64px",
        }}
      >
        <Stack spacing="40px">
          {/* CustomTypography 의 기본 px(4px)와 좌측 라인을 맞춘다 */}
          <Box sx={{ pl: "4px" }}>
            <LogoWithTextSvg />
          </Box>

          <Stack spacing="24px">
            {letter.greeting && (
              <CustomTypography
                variant="Subtitle"
                fontWeight={700}
                sx={{
                  color: theme.vars.palette.Content.Strong,
                  lineHeight: "150%",
                }}
              >
                {letter.greeting}
              </CustomTypography>
            )}

            {letter.paragraphsBeforeVideo.map((paragraph) => (
              <CustomTypography
                key={paragraph}
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: "170%",
                }}
              >
                {renderParagraphWithChannelLink(
                  paragraph,
                  letterId ?? "",
                  trackId
                )}
              </CustomTypography>
            ))}

            <YoutubeEmbed videoId={letter.youtubeVideoId} />

            {letter.paragraphsAfterVideo.map((paragraph) => (
              <CustomTypography
                key={paragraph}
                variant="Body"
                sx={{
                  color: theme.vars.palette.Content.Medium,
                  lineHeight: "170%",
                }}
              >
                {renderParagraphWithChannelLink(
                  paragraph,
                  letterId ?? "",
                  trackId
                )}
              </CustomTypography>
            ))}

            {previousLetterIds.length > 0 && (
              <Stack spacing="8px">
                {previousLetterIds.map((id) => (
                  <CustomTypography
                    key={id}
                    variant="Body"
                    sx={{
                      color: theme.vars.palette.Content.Medium,
                      lineHeight: "170%",
                    }}
                  >
                    <Box
                      component="a"
                      // 전체 페이지 이동이라 대상 레터의 식별(?u=)·진입 트래킹·스크롤 초기화가 그대로 동작한다
                      href={`/letter/${id}${trackId ? `?u=${trackId}` : ""}`}
                      sx={{
                        // 본문과 같은 색 (Highlight 링크색 아님) + 언더라인만
                        color: theme.vars.palette.Content.Medium,
                        textDecoration: "underline",
                        textUnderlineOffset: "3px",
                      }}
                    >
                      {`${LETTER_ORDINALS[id] ?? id}번째 레터 다시보기: ${LETTERS[id].title}`}
                    </Box>
                  </CustomTypography>
                ))}
              </Stack>
            )}
          </Stack>

          {!hasKakaoAsk && (
            // 본문 텍스트의 좌우 라인(CustomTypography 기본 px 4px)과 버튼 폭을 맞춘다
            <Box sx={{ px: "4px" }}>
              <Button
                onClick={() => {
                  if (!trackId) {
                    window.location.href = deepLinkData.install.url;
                    return;
                  }
                  trackCtaClick();
                  // 전체 페이지 이동으로 track 요청이 유실되지 않도록 잠시 뒤에 이동
                  setTimeout(() => {
                    window.location.href = deepLinkData.install.url;
                  }, 300);
                }}
                variant="Solid"
                color="primary"
                size="lg"
                sx={{
                  width: "100%",
                  height: "54px",
                  fontWeight: 700,
                  borderRadius: "12px",
                  fontSize: "16px",
                }}
              >
                디스턴싱 시작하기
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </FullPageContainer>
  );
}
